/* eslint-disable no-unused-vars */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonPagination from 'components/commonPagination/CommonPagination';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListOrderData } from 'reactRedux/thunks/orderThunk/orderThunk';
import {
  rentalStatusMap,
  paymentStatusMap,
  paymentStatusColors,
  customStaticRanges,
} from 'utils/StaticData';
import CommonTable from '../../../components/commonTable/CommonTable';
import { SyncDataOrders } from 'reactRedux/thunks/orderThunk/orderThunk';
import SyncDataLoader from 'components/wholePageLoader/SyncDataLoader';
import * as dateFns from 'date-fns';
import OrderListFilter from './OrderListFilter';
import useListState from 'hooks/useListState';
import ExportButton from 'components/commonExport/ExportButton';

const ORDER_EXPORT_MATCH_TABLE_KEYS = [
  'orderId',
  'intentId',
  'rentalStatus',
  'paymentStatus',
  'rentTime',
  'captureDate',
  'rentalMerchant',
  'rentalStationId',
  'last4Digits',
];

const OrderList = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const { adminDetails } = useSelector((s) => s.authSlice);
  const { isLoading, orderListData, isSyncLoading } = useSelector(
    (s) => s.OrderSlice,
  );

  // Default date strings computed once at mount
  const defaultStartDate = dateFns.format(
    dateFns.addDays(new Date(), -6),
    'yyyy-MM-dd',
  );
  const defaultEndDate = dateFns.format(new Date(), 'yyyy-MM-dd');

  const {
    params,
    setParam,
    setParams,
    debouncedParams,
    page,
    setPage,
    resetAll,
    currentUrl,
  } = useListState({
    defaults: {
      search: '',
      last4: '',
      paymentStatus: '',
      rentalStatus: '',
      sortKey: 'rentTime',
      sortBy: -1,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      calendarLabel: 'Last 7 days',
      limit: 10,
      returnStartDate: '',
      returnEndDate: '',
      returnCalendarLabel: 'All Time',
      captureDateStart: '',
      captureDateEnd: '',
      captureCalendarLabel: 'All Time',
    },
    debounceKeys: ['search', 'last4'],
  });

  // Local state — UI-only, not persisted in URL
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(() => [
    {
      startDate: dateFns.parseISO(params.startDate),
      endDate: dateFns.parseISO(params.endDate),
      key: 'selection',
    },
  ]);
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] = useState(
    params.calendarLabel,
  );
  const [merchant, setMerchant] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [couponIdFilter, setCouponIdFilter] = useState(null);

  // Return Time filter state
  const [showReturnCalendar, setShowReturnCalendar] = useState(false);
  const [tempReturnDateRange, setTempReturnDateRange] = useState([
    {
      startDate: params.returnStartDate
        ? dateFns.parseISO(params.returnStartDate)
        : dateFns.addDays(new Date(), -30),
      endDate: params.returnEndDate
        ? dateFns.parseISO(params.returnEndDate)
        : new Date(),
      key: 'selection',
    },
  ]);
  const [tempReturnCalendarFilterTitle, setTempReturnCalendarFilterTitle] =
    useState(params.returnCalendarLabel);

  // Capture (payment charge) date filter state
  const [showCaptureCalendar, setShowCaptureCalendar] = useState(false);
  const [tempCaptureDateRange, setTempCaptureDateRange] = useState([
    {
      startDate: params.captureDateStart
        ? dateFns.parseISO(params.captureDateStart)
        : dateFns.addDays(new Date(), -30),
      endDate: params.captureDateEnd
        ? dateFns.parseISO(params.captureDateEnd)
        : new Date(),
      key: 'selection',
    },
  ]);
  const [tempCaptureCalendarFilterTitle, setTempCaptureCalendarFilterTitle] =
    useState(params.captureCalendarLabel);

  // ── Date range picker handlers ─────────────────────────────────────────────

  const handleSelect = (ranges) => {
    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );
    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  };

  const handleApply = () => {
    const startDate = dateFns.format(tempDateRange[0].startDate, 'yyyy-MM-dd');
    const endDate = dateFns.format(tempDateRange[0].endDate, 'yyyy-MM-dd');
    setParams({ startDate, endDate, calendarLabel: tempCalendarFilterTitle });
    setShowCalendar(false);
  };

  const handleClear = () => {
    const defaultRange = [
      {
        startDate: dateFns.addDays(new Date(), -6),
        endDate: new Date(),
        key: 'selection',
      },
    ];
    setTempDateRange(defaultRange);
    setParams({
      startDate: dateFns.format(dateFns.addDays(new Date(), -6), 'yyyy-MM-dd'),
      endDate: dateFns.format(new Date(), 'yyyy-MM-dd'),
      calendarLabel: 'Last 7 days',
    });
    setTempCalendarFilterTitle('Last 7 days');
    setShowCalendar(false);
  };

  // Return Time filter handlers
  const handleReturnSelect = (ranges) => {
    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );
    setTempReturnCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempReturnDateRange([ranges.selection]);
  };

  const handleReturnApply = () => {
    const returnStartDate = dateFns.format(
      tempReturnDateRange[0].startDate,
      'yyyy-MM-dd',
    );
    const returnEndDate = dateFns.format(
      tempReturnDateRange[0].endDate,
      'yyyy-MM-dd',
    );
    setParams({
      returnStartDate,
      returnEndDate,
      returnCalendarLabel: tempReturnCalendarFilterTitle,
    });
    setShowReturnCalendar(false);
  };

  const handleReturnClear = () => {
    const defaultRange = [
      {
        startDate: dateFns.addDays(new Date(), -30),
        endDate: new Date(),
        key: 'selection',
      },
    ];
    setTempReturnDateRange(defaultRange);
    setParams({
      returnStartDate: '',
      returnEndDate: '',
      returnCalendarLabel: 'All Time',
    });
    setTempReturnCalendarFilterTitle('All Time');
    setShowReturnCalendar(false);
  };

  // Capture (payment charge) date filter handlers
  const handleCaptureSelect = (ranges) => {
    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );
    setTempCaptureCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempCaptureDateRange([ranges.selection]);
  };

  const handleCaptureApply = () => {
    const captureDateStart = dateFns.format(
      tempCaptureDateRange[0].startDate,
      'yyyy-MM-dd',
    );
    const captureDateEnd = dateFns.format(
      tempCaptureDateRange[0].endDate,
      'yyyy-MM-dd',
    );
    setParams({
      captureDateStart,
      captureDateEnd,
      captureCalendarLabel: tempCaptureCalendarFilterTitle,
    });
    setShowCaptureCalendar(false);
  };

  const handleCaptureClear = () => {
    const defaultRange = [
      {
        startDate: dateFns.addDays(new Date(), -30),
        endDate: new Date(),
        key: 'selection',
      },
    ];
    setTempCaptureDateRange(defaultRange);
    setParams({
      captureDateStart: '',
      captureDateEnd: '',
      captureCalendarLabel: 'All Time',
    });
    setTempCaptureCalendarFilterTitle('All Time');
    setShowCaptureCalendar(false);
  };

  // ── Reset all filters ──────────────────────────────────────────────────────

  const handleReset = () => {
    resetAll();
    setMerchant('');
    setMerchantId('');
    setCouponIdFilter(null);
    setShowCalendar(false);
    setShowReturnCalendar(false);
    setShowCaptureCalendar(false);
    const defaultRange = [
      {
        startDate: dateFns.addDays(new Date(), -6),
        endDate: new Date(),
        key: 'selection',
      },
    ];
    setTempDateRange(defaultRange);
    setTempCalendarFilterTitle('Last 7 days');
    // Reset return date filter
    const defaultReturnRange = [
      {
        startDate: dateFns.addDays(new Date(), -30),
        endDate: new Date(),
        key: 'selection',
      },
    ];
    setTempReturnDateRange(defaultReturnRange);
    setTempReturnCalendarFilterTitle('All Time');
    // Reset capture date filter
    const defaultCaptureRange = [
      {
        startDate: dateFns.addDays(new Date(), -30),
        endDate: new Date(),
        key: 'selection',
      },
    ];
    setTempCaptureDateRange(defaultCaptureRange);
    setTempCaptureCalendarFilterTitle('All Time');
    navigate('.', { replace: true, state: null });
  };

  // RAT-1865: same filter shape the list page already fetches with, plus
  // last4/couponId (the old export silently dropped these two even though
  // the list view and backend both support them).
  const exportFilters = useMemo(
    () => ({
      merchant: debouncedParams.search,
      merchantId,
      last4: debouncedParams.last4,
      paymentStatus: params.paymentStatus,
      rentalStatus: params.rentalStatus,
      start: params.startDate,
      end: params.endDate,
      returnStart: params.returnStartDate,
      returnEnd: params.returnEndDate,
      couponId: couponIdFilter,
      captureDateStart: params.captureDateStart,
      captureDateEnd: params.captureDateEnd,
    }),
    [
      debouncedParams.search,
      debouncedParams.last4,
      merchantId,
      params.paymentStatus,
      params.rentalStatus,
      params.startDate,
      params.endDate,
      params.returnStartDate,
      params.returnEndDate,
      couponIdFilter,
      params.captureDateStart,
      params.captureDateEnd,
    ],
  );

  // Handle items per page change
  const handleItemsPerPageChange = (newLimit) => {
    setParams({ limit: newLimit, page: 1 });
  };

  // ── API fetch ──────────────────────────────────────────────────────────────

  const getOrderList = useCallback(() => {
    dispatch(
      ListOrderData({
        page,
        limit: params.limit || 10,
        merchant: debouncedParams.search,
        last4: debouncedParams.last4,
        merchantId,
        paymentStatus: params.paymentStatus,
        rentalStatus: params.rentalStatus,
        sortKey: params.sortKey,
        sortBy: params.sortBy,
        start: params.startDate,
        end: params.endDate,
        couponId: couponIdFilter,
        returnStart: params.returnStartDate,
        returnEnd: params.returnEndDate,
        captureDateStart: params.captureDateStart,
        captureDateEnd: params.captureDateEnd,
      }),
    );
  }, [
    dispatch,
    page,
    params.limit,
    debouncedParams.search,
    debouncedParams.last4,
    merchantId,
    params.paymentStatus,
    params.rentalStatus,
    params.sortKey,
    params.sortBy,
    params.startDate,
    params.endDate,
    couponIdFilter,
    params.returnStartDate,
    params.returnEndDate,
    params.captureDateStart,
    params.captureDateEnd,
  ]);

  // ── Cross-page navigation state ────────────────────────────────────────────

  useEffect(() => {
    if (state?.couponId) {
      setCouponIdFilter(state.couponId);
    }
  }, [state?.couponId]);

  useEffect(() => {
    if (state?.merchant) {
      setParam('search', state.merchant);
      setMerchant(state.merchant);
      setMerchantId(state.merchantId);
    }
  }, [state?.merchant]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Main fetch effect ──────────────────────────────────────────────────────

  useEffect(() => {
    getOrderList();
  }, [getOrderList]);

  // ── Sync data ──────────────────────────────────────────────────────────────

  const handleSyncData = () => {
    setPage(1);
    dispatch(SyncDataOrders(() => getOrderList()));
  };

  // ── Table columns ──────────────────────────────────────────────────────────

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.orderId, {
      id: 'orderId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Order ID
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.intentId, {
      id: 'intentId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          PaymentIntent
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.rentalStatus, {
      id: 'rentalStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Rental Status
        </Text>
      ),
      cell: (info) => {
        const statusValue = info.getValue();
        const statusText = rentalStatusMap[statusValue] || 'N/A';
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow={'hidden'}
            textOverflow={'ellipsis'}
          >
            {statusText}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.paymentStatus, {
      id: 'paymentStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Payment Status
        </Text>
      ),
      cell: (info) => (
        <Text
          color={paymentStatusColors[info.getValue()] || 'gray.500'}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {paymentStatusMap[info.getValue()] || 'Unknown'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.rentTime, {
      id: 'rentTime',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Rent Time
        </Text>
      ),
      cell: (info) => {
        const rentTime = info.getValue();
        const formattedDateTime = moment
          .parseZone(rentTime)
          .format('DD-MM-YYYY HH:mm:ss');
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow={'hidden'}
            textOverflow={'ellipsis'}
          >
            {formattedDateTime}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.capturedDate, {
      id: 'capturedDate',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Capture Date
        </Text>
      ),
      cell: (info) => {
        const capturedDate = info.getValue();
        const formattedDateTime = capturedDate
          ? moment.parseZone(capturedDate).format('DD-MM-YYYY HH:mm:ss')
          : '-';
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow={'hidden'}
            textOverflow={'ellipsis'}
          >
            {formattedDateTime}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.rentalMerchant, {
      id: 'rentalMerchant',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Rental Merchant
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.rentalStationId, {
      id: 'rentalStationId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Rental Stations
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.last4, {
      id: 'last4',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Last 4
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue()}
        </Text>
      ),
    }),
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {isSyncLoading && <SyncDataLoader />}
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <OrderListFilter
          search={params.search}
          setSearch={(val) => setParam('search', val)}
          last4Search={params.last4}
          setLast4Search={(val) => setParam('last4', val)}
          paymentStatus={params.paymentStatus}
          setPaymentStatus={(val) => setParam('paymentStatus', val)}
          rentalStatus={params.rentalStatus}
          setRentalStatus={(val) => setParam('rentalStatus', val)}
          handleSyncData={handleSyncData}
          adminDetails={adminDetails}
          setPage={setPage}
          setShowCalendar={setShowCalendar}
          state={state}
          showCalendar={showCalendar}
          tempDateRange={tempDateRange}
          setTempDateRange={setTempDateRange}
          calendarFilterTitle={params.calendarLabel}
          handleApply={handleApply}
          handleClear={handleClear}
          handleSelect={handleSelect}
          exportButton={
            <ExportButton
              entity="order"
              title="Export Orders"
              currentFilters={exportFilters}
              filteredTotal={orderListData?.total}
              matchTableKeys={ORDER_EXPORT_MATCH_TABLE_KEYS}
              width="150px"
            />
          }
          onReset={handleReset}
          // Return Time filter props
          showReturnCalendar={showReturnCalendar}
          setShowReturnCalendar={setShowReturnCalendar}
          tempReturnDateRange={tempReturnDateRange}
          setTempReturnDateRange={setTempReturnDateRange}
          returnCalendarFilterTitle={params.returnCalendarLabel}
          handleReturnApply={handleReturnApply}
          handleReturnClear={handleReturnClear}
          handleReturnSelect={handleReturnSelect}
          // Capture date filter props
          showCaptureCalendar={showCaptureCalendar}
          setShowCaptureCalendar={setShowCaptureCalendar}
          tempCaptureDateRange={tempCaptureDateRange}
          setTempCaptureDateRange={setTempCaptureDateRange}
          captureCalendarFilterTitle={params.captureCalendarLabel}
          handleCaptureApply={handleCaptureApply}
          handleCaptureClear={handleCaptureClear}
          handleCaptureSelect={handleCaptureSelect}
        />
        <CommonTable
          columns={columns}
          data={orderListData?.list}
          isLoading={isLoading}
          isLink={true}
          parentLink="/admin/orders/details"
          keyValue="orderId"
          getfilter={(e) => setParams({ sortKey: e.sortKey, sortBy: e.sortBy })}
          defaultFilter={{ sortBy: params.sortBy, sortKey: params.sortKey }}
          rowclick={['actions']}
          dynamicWidth={{
            orderId: '120px',
            intentId: '300px',
            rentalStatus: '170px',
            paymentStatus: '150px',
            rentTime: '200px',
            capturedDate: '200px',
            rentalMerchant: '200px',
            rentalStationId: '150px',
            last4: '100px',
          }}
          excludes={[
            'rentalStatus',
            'paymentStatus',
            'rentTime',
            'rentalStationId',
          ]}
          isSorting={true}
        />
        <CommonPagination
          totalCount={orderListData?.total}
          activePage={page}
          pageCount={Math.ceil(orderListData?.total / orderListData?.limit)}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Box>
    </>
  );
};

export default OrderList;
